import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';

interface Props {
    delay: number;
    children: ReactNode;
}
type AnyFunction = (...args: unknown[]) => any;

/**
 * A component that delays rendering its children for a specified amount of time.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {number} props.delay - The delay in milliseconds before rendering the children.
 * @param {ReactNode} props.children - The content to be rendered after the delay.
 *
 * @example
 * <DelayedRender delay={1000}>
 *   <div>This content will appear after 1 second</div>
 * </DelayedRender>
 */
export const DelayedRender: FC<Props> = ({ delay, children }) => {
    const delayedRender = useDelayedRender(delay);

    return delayedRender(() => children);
};

function useDelayedRender(delay: number) {
    const [delayed, setDelayed] = useState(true);
    useEffect(() => {
        const timeout = setTimeout(() => setDelayed(false), delay);
        return () => clearTimeout(timeout);
    }, [delay]);

    return (fn: AnyFunction) => !delayed && fn();
}
